import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isV2PatientVersion } from '@odo/shared-ui';
import {
    type WorkQueueCase,
    type WorkflowMeta,
    forceWorkflowStatus
} from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { useToast } from '../../providers/ToastProvider';
import { reportError } from '../../services/errorReporting';
import { apiErrorMessage } from '../../utils/apiError';
import { usePracticeFeatureFlags } from '../../hooks/usePracticeFeatureFlags';
import { REFINEMENT_DESIGN_APPROVAL_FEATURE } from '../../services/practiceFeatureFlags';
import { PHASES, PENDING_REFINEMENT_APPROVAL_STATUS_ID, V2_ONLY_STATUS_IDS } from './constants';
import { forceStatusTargetsByPhase } from './forceStatusTargets';
import { invalidateCaseDetail } from './util';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Steps this case can't reach, kept listed but greyed out with the reason.
function unreachableStepNote(
    statusId: string,
    canEnterPendingApproval: boolean,
    version: string | null
): string | null {
    if (statusId === PENDING_REFINEMENT_APPROVAL_STATUS_ID && !canEnterPendingApproval) {
        return ' (feature disabled)';
    }
    if (V2_ONLY_STATUS_IDS.has(statusId) && !isV2PatientVersion(version)) {
        return ' (V2 cases only)';
    }
    return null;
}

interface ForceStatusModalProps {
    workCase: WorkQueueCase;
    meta: WorkflowMeta | undefined;
    practiceId: string | undefined;
    onClose: () => void;
    onForced?: () => void;
}

export function ForceStatusModal({
    workCase: c,
    meta,
    practiceId,
    onClose,
    onForced
}: ForceStatusModalProps) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const catalog = useStatusCatalog();
    const [phase, setPhase] = useState<string>('');
    const [toStatus, setToStatus] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    useEscapeKey(onClose, !submitting);

    // Fails closed until the flag is confirmed enabled. Ref-6.1 stays
    // visible-but-blocked rather than flashing "allowed" while this loads.
    const { features: practiceFeatures } = usePracticeFeatureFlags(practiceId);
    const hasRefinementDesignApproval = practiceFeatures.includes(
        REFINEMENT_DESIGN_APPROVAL_FEATURE.key
    );
    const canEnterPendingApproval = hasRefinementDesignApproval || isV2PatientVersion(c.version);
    const pendingApprovalBlocked =
        !canEnterPendingApproval && toStatus === PENDING_REFINEMENT_APPROVAL_STATUS_ID;

    const reasonValid = reason.trim().length >= 5;
    const targetValid = toStatus.length > 0 && toStatus !== c.statusId;
    const valid = reasonValid && targetValid && !pendingApprovalBlocked;

    const handleSubmit = async (): Promise<void> => {
        if (!valid) return;
        setSubmitting(true);
        try {
            await forceWorkflowStatus(c.id, toStatus, reason.trim());
            await invalidateCaseDetail(queryClient, c.id);
            showToast(`Forced ${c.name} to ${catalog.display(toStatus)}`, 'success');
            onClose();
            onForced?.();
        } catch (err) {
            const diagnosticMessage = err instanceof Error ? err.message : 'Unknown error';
            showToast(apiErrorMessage(err, 'Failed to force status. Please try again.'), 'error');
            reportError({
                source: 'ForceStatusModal.handleSubmit',
                message: diagnosticMessage,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id, toStatus, reason }
            });
            setSubmitting(false);
        }
    };

    const visibleStepsByPhase = forceStatusTargetsByPhase(meta?.statuses ?? []);

    const phaseRank = (p: string): number => {
        const i = (PHASES as readonly string[]).indexOf(p);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const phases = Object.keys(visibleStepsByPhase).sort((a, b) => phaseRank(a) - phaseRank(b));
    const stepsForPhase = phase
        ? [...(visibleStepsByPhase[phase] ?? [])].sort((a, b) => a.order - b.order)
        : [];

    const handlePhaseChange = (next: string): void => {
        setPhase(next);
        setToStatus('');
    };

    const selectClass =
        'w-full rounded-[10px] border-[1.5px] border-[rgb(233,234,238)] px-3 py-2.5 text-[13px] text-gray-800 ' +
        'focus:outline-none focus:border-[rgb(217,119,6)] focus:ring-2 focus:ring-[rgba(217,119,6,0.15)] ' +
        'transition-colors disabled:opacity-50 disabled:bg-[rgb(250,250,251)]';

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 font-inter">
            {/* Inert backdrop — dismiss only via Cancel / X / Escape, never a stray
          click-out, so an in-progress override target + reason can't be lost. */}
            <div className="absolute inset-0 bg-[rgba(17,24,39,0.42)] backdrop-blur-[2px]" />
            <div className="relative w-[min(520px,100%)] max-h-[calc(100vh-48px)] bg-white rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.28)] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-start gap-3 px-[22px] pt-5 pb-4">
                    <div className="w-[38px] h-[38px] rounded-[10px] bg-[rgb(254,243,224)] flex items-center justify-center flex-shrink-0">
                        <ExclamationTriangleIcon className="w-[19px] h-[19px] text-[rgb(180,100,20)]" />
                    </div>
                    <div className="flex-1 min-w-0 pt-px">
                        <div className="text-[17px] font-bold text-[rgb(24,28,36)] tracking-[-0.01em]">
                            Force status (admin override)
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="w-[18px] h-[18px]" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-[22px] pb-2 space-y-4">
                    <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-[10px] bg-[rgb(254,243,224)] border border-[rgb(245,213,160)]">
                        <ExclamationTriangleIcon className="w-4 h-4 text-[rgb(180,100,20)] flex-shrink-0 mt-px" />
                        <div className="text-[12.5px] text-[rgb(124,84,24)] leading-relaxed">
                            This bypasses the workflow engine. The case will jump to the chosen
                            status without firing any normal action. Use only for ops fixes.
                        </div>
                    </div>

                    <div className="text-xs text-gray-600">
                        <strong>{c.name}</strong>
                    </div>
                    <div className="flex items-center px-3 py-2.5 rounded-[10px] border border-[rgb(238,239,243)] bg-[rgb(250,250,251)]">
                        <span className="flex-1 min-w-0">
                            <span className="block text-[10px] font-bold text-[rgb(140,146,158)] tracking-[0.05em]">
                                CURRENTLY AT
                            </span>
                            <span className="block text-[13.5px] font-semibold text-gray-800 truncate">
                                {catalog.display(c.statusId)}
                            </span>
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[11px] font-bold text-[rgb(120,126,138)] tracking-[0.05em] mb-2">
                                Phase
                            </div>
                            <select
                                value={phase}
                                onChange={(e) => handlePhaseChange(e.target.value)}
                                disabled={submitting}
                                className={selectClass}
                            >
                                <option value="">- choose a phase -</option>
                                {phases.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <div className="text-[11px] font-bold text-[rgb(120,126,138)] tracking-[0.05em] mb-2">
                                Step
                            </div>
                            <select
                                value={toStatus}
                                onChange={(e) => setToStatus(e.target.value)}
                                disabled={submitting || !phase}
                                className={selectClass}
                            >
                                <option value="">
                                    {phase ? '- choose a step -' : '- pick a phase first -'}
                                </option>
                                {stepsForPhase.map((s) => {
                                    const gateNote = unreachableStepNote(
                                        s.id,
                                        canEnterPendingApproval,
                                        c.version
                                    );
                                    return (
                                        <option
                                            key={s.id}
                                            value={s.id}
                                            disabled={s.id === c.statusId || gateNote !== null}
                                            className={gateNote ? 'text-gray-400' : undefined}
                                        >
                                            {catalog.display(s.id)}
                                            {s.id === c.statusId ? ' (current)' : ''}
                                            {gateNote}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>

                    {pendingApprovalBlocked && (
                        <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-[10px] bg-red-50 border border-red-200">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-px" />
                            <div className="text-[12.5px] text-red-700 leading-relaxed">
                                This practice doesn&apos;t have the &quot;
                                {REFINEMENT_DESIGN_APPROVAL_FEATURE.label}&quot; feature enabled, so
                                it can&apos;t reach this step. Enable it from Accounts → Practice →
                                Beta access, or choose a different step.
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="text-[11px] font-bold text-[rgb(120,126,138)] tracking-[0.05em] mb-2">
                            Reason (audit trail)
                        </div>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Why is this manual override needed? Will be persisted forever on the timeline."
                            style={{ minHeight: 96 }}
                            disabled={submitting}
                            className="w-full rounded-[10px] border-[1.5px] border-[rgb(233,234,238)] px-3.5 py-3 text-[13.5px] text-gray-800 placeholder-[rgb(173,178,189)] resize-none focus:outline-none focus:border-[rgb(217,119,6)] focus:ring-2 focus:ring-[rgba(217,119,6,0.15)] transition-colors disabled:opacity-50"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2.5 px-[22px] py-3.5 border-t border-[rgb(238,239,243)] bg-[rgb(250,250,251)]">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="h-10 px-[18px] rounded-[9px] border border-[rgb(216,219,224)] bg-white text-[13.5px] font-semibold text-[rgb(75,82,96)] hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => void handleSubmit()}
                        disabled={!valid || submitting}
                        className={`h-10 px-5 rounded-[9px] inline-flex items-center gap-2 text-[13.5px] font-bold text-white transition-colors ${
                            valid && !submitting
                                ? 'bg-[rgb(217,119,6)] hover:bg-[rgb(180,100,20)] shadow-[0_1px_2px_rgba(180,100,20,0.3)]'
                                : 'bg-[rgb(245,222,179)] cursor-not-allowed'
                        }`}
                    >
                        {submitting ? (
                            <>
                                <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                Forcing…
                            </>
                        ) : (
                            'Force status'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
